import React, { useState, useEffect } from 'react';
import { Key, Save, PlayCircle, Info, RefreshCw } from 'lucide-react';
import { useContract } from '../../hooks/useContract';
import { useGameState } from '../../hooks/useGameState';
import { gameDB } from '../../services/db';
import toast from 'react-hot-toast';

interface ConfigTabProps {
  inputClasses: string;
  currentWallet: string;
}

export function ConfigTab({ inputClasses, currentWallet }: ConfigTabProps) {
  const [infuraKey, setInfuraKey] = useState('');
  const [etherscanKey, setEtherscanKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [contractAddress, setContractAddress] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [conversionRate, setConversionRate] = useState(100);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const { updateConversionRate, isDemoMode, toggleDemoMode } = useContract();
  const { resetGameState } = useGameState(currentWallet);

  // Load saved configurations for this account
  useEffect(() => {
    if (currentWallet) {
      setInfuraKey(localStorage.getItem(`infuraKey_${currentWallet}`) || '');
      setEtherscanKey(localStorage.getItem(`etherscanKey_${currentWallet}`) || '');
      setPrivateKey(localStorage.getItem(`adminPrivateKey_${currentWallet}`) || '');
      setContractAddress(localStorage.getItem(`contractAddress_${currentWallet}`) || '');
      setConversionRate(parseInt(localStorage.getItem(`conversionRate_${currentWallet}`) || '100'));
    }
  }, [currentWallet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!currentWallet) {
        toast.error('Wallet not connected');
        return;
      }

      // Validate contract address format
      if (contractAddress && !contractAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
        toast.error('Invalid contract address format');
        return;
      }

      // Save configurations to localStorage with wallet address
      localStorage.setItem(`infuraKey_${currentWallet}`, infuraKey);
      localStorage.setItem(`etherscanKey_${currentWallet}`, etherscanKey);
      localStorage.setItem(`adminPrivateKey_${currentWallet}`, privateKey);
      localStorage.setItem(`contractAddress_${currentWallet}`, contractAddress);
      localStorage.setItem(`conversionRate_${currentWallet}`, conversionRate.toString());

      // Update contract address in the application
      if (contractAddress) {
        localStorage.setItem('CRYPTO_CLICKER_ADDRESS', contractAddress);
      }

      // Update conversion rate in contract if deployed
      if (contractAddress) {
        await updateConversionRate(conversionRate);
      }
      
      toast.success('Configuration saved successfully');
      
      // Reload page to apply new configuration
      window.location.reload();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Error saving configuration');
    }
  };

  const handleReset = async () => {
    try {
      await resetGameState();
      
      if (currentWallet) {
        localStorage.removeItem(`infuraKey_${currentWallet}`);
        localStorage.removeItem(`etherscanKey_${currentWallet}`);
        localStorage.removeItem(`adminPrivateKey_${currentWallet}`);
        localStorage.removeItem(`contractAddress_${currentWallet}`);
        localStorage.removeItem(`conversionRate_${currentWallet}`);
        localStorage.removeItem('CRYPTO_CLICKER_ADDRESS');
        
        await gameDB.clearData(currentWallet);
      }

      toast.success('All data has been reset');
      setShowResetConfirm(false);
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error resetting data:', error);
      toast.error('Error resetting data');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Demo Mode */}
      <div className="bg-blue-900/20 rounded-lg p-6 border border-blue-500/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-300">Demo Mode</h3>
            <p className="text-sm text-blue-200/70">
              {isDemoMode 
                ? "Game runs with default values, no deployed contract" 
                : "Game requires a deployed contract to function"}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleDemoMode}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDemoMode
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <PlayCircle className="inline-block mr-2" size={20} />
            {isDemoMode ? 'Disable Demo Mode' : 'Enable Demo Mode'}
          </button>
        </div>
      </div>

      {/* Reset */}
      <div className="bg-red-900/20 rounded-lg p-6 border border-red-500/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-red-300">Reset</h3>
            <p className="text-sm text-red-200/70">
              Warning: This will erase all game data and configurations
            </p>
          </div>
          {!showResetConfirm ? (
            <button
              type="button"
              onClick={() => setShowResetConfirm(true)}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              <RefreshCw className="inline-block mr-2" size={20} />
              Reset All
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-500 hover:bg-gray-600 text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Confirm
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contract Address */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Contract Address
        </label>
        <input
          type="text"
          value={contractAddress}
          onChange={(e) => setContractAddress(e.target.value)}
          className={inputClasses}
          placeholder="0x..."
          pattern="^0x[a-fA-F0-9]{40}$"
          title="Valid Ethereum address (0x followed by 40 hexadecimal characters)"
          disabled={isDemoMode}
        />
        <p className="mt-1 text-sm text-gray-400">
          Format: 0x followed by 40 hexadecimal characters
        </p>
      </div>

      {/* Infura API Key */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="infura-key" className="block text-sm font-medium text-gray-300">
            Infura API Key
          </label>
          <div className="group relative">
            <Info size={16} className="text-gray-400 cursor-help" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-xs rounded shadow-lg">
              You can find your Infura API key in your project settings on infura.io
            </div>
          </div>
        </div>
        <input
          id="infura-key"
          type="text"
          value={infuraKey}
          onChange={(e) => setInfuraKey(e.target.value)}
          className={inputClasses}
          placeholder="Enter your Infura API key"
          disabled={isDemoMode}
        />
        <p className="mt-1 text-sm text-gray-400">
          Format: 32 character string
        </p>
      </div>

      {/* Etherscan API Key */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor="etherscan-key" className="block text-sm font-medium text-gray-300">
            Etherscan API Key
          </label>
          <div className="group relative">
            <Info size={16} className="text-gray-400 cursor-help" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-xs rounded shadow-lg">
              You can get your Etherscan API key from your account on etherscan.io
            </div>
          </div>
        </div>
        <input
          id="etherscan-key"
          type="text"
          value={etherscanKey}
          onChange={(e) => setEtherscanKey(e.target.value)}
          className={inputClasses}
          placeholder="Enter your Etherscan API key"
          disabled={isDemoMode}
        />
        <p className="mt-1 text-sm text-gray-400">
          Format: 34 character string starting with "NPTX"
        </p>
      </div>

      {/* Admin Private Key */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Admin Private Key
        </label>
        <div className="relative">
          <input
            type={showPrivateKey ? "text" : "password"}
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white pr-10"
            placeholder="Enter your admin private key"
            disabled={isDemoMode}
          />
          <button
            type="button"
            onClick={() => setShowPrivateKey(!showPrivateKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
            disabled={isDemoMode}
          >
            <Key size={20} />
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-400">
          64 hexadecimal characters without '0x'
        </p>
      </div>

      {/* Conversion Rate */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Conversion Rate (COINS per WEI)
        </label>
        <input
          type="number"
          value={conversionRate}
          onChange={(e) => setConversionRate(parseInt(e.target.value))}
          className={inputClasses}
          min="1"
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          <Save size={20} />
          Save Configuration
        </button>
      </div>
    </form>
  );
}